import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { Check, ChevronDown, ChevronLeft, ChevronRight, RefreshCw, Users, Lock, AlertTriangle, SlidersHorizontal } from 'lucide-react'
import ProfilePhoto from '../../../../../components/ProfilePhoto'
import TooltipWrapper from '../../../../../components/TooltipWrapper'
import { useInboxStore } from '../../../../../stores/inbox/useInboxStore'
import { useInboxHandlers } from '../../../../../services/actions/inbox/useInboxHandlers'
import { AppRoutes } from '../../../../../interfaces/store/appStore'
import type { ISharedInboxAccount } from '../../../../../services/apis/sharedInbox/useSharedInboxRequests'

/** How often the open shared inbox re-reads accounts and its first page. */
const POLL_MS = 60_000
/** Faster while an account's first sync is running, so its conversations show up as they land. */
const POLL_SYNCING_MS = 15_000

const initials = (name: string) =>
    name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]?.toUpperCase()).join('') || 'LI'

const statusLabel = (status: string) => {
    if (status === 'connected') return ''
    if (status === 'credentials') return 'Reconnect required'
    if (status === 'not_found') return 'Disconnected'
    return 'Unavailable'
}

const syncLabel = (account: ISharedInboxAccount) => {
    if (account.sync?.status === 'running') return 'Syncing…'
    if (account.sync?.lastSyncedAt) return `Synced ${moment(account.sync.lastSyncedAt).fromNow()}`
    return 'Not synced yet'
}

/**
 * Which proxy accounts the shared inbox shows: all of them, or one.
 *
 * Two views in one popover. The list is only for choosing — name, whose account it is,
 * whether the team sees it, unread count. Who may read an account and re-syncing it live
 * one step away under "Sharing & sync", and only for the people who manage the account.
 *
 * Also owns the shared inbox's polling — account unread counts, sync progress and the
 * list's first page — because new messages arrive server-side, not through this browser.
 */
const SharedAccountsPicker: React.FC = () => {
    const [open, setOpen] = useState(false)
    const [view, setView] = useState<'accounts' | 'manage'>('accounts')
    const [loaded, setLoaded] = useState(false)
    const rootRef = useRef<HTMLDivElement>(null)
    const navigate = useNavigate()

    const { store: { sharedAccounts, sharedAccountFilter, conversations } } = useInboxStore()
    const handlers = useInboxHandlers()
    const {
        loadSharedAccounts,
        setSharedAccountFilter,
        setSharedAccountSharingHandler,
        syncSharedAccountHandler,
    } = handlers

    // The interval outlives renders; always call the newest handlers (their filters are current).
    const handlersRef = useRef(handlers)
    handlersRef.current = handlers

    const accounts: ISharedInboxAccount[] = useMemo(() => sharedAccounts ?? [], [sharedAccounts])
    const manageable = accounts.filter(a => a.canManage)
    // A disconnected account never finishes a sync — it must not keep the fast poll going.
    const awaitingFirstSync = accounts.some(a => a.status === 'connected' && !a.sync?.backfilledAt)
    const anySyncing = awaitingFirstSync || accounts.some(a => a.sync?.status === 'running')
    const totalUnread = accounts.reduce((sum, a) => sum + (a.unreadCount || 0), 0)
    const selected = accounts.find(a => sharedAccountFilter?.[0] === a.accountId) ?? null
    const triggerUnread = selected ? selected.unreadCount : totalUnread

    useEffect(() => {
        let cancelled = false
        loadSharedAccounts().finally(() => { if (!cancelled) setLoaded(true) })
        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const id = window.setInterval(() => {
            void handlersRef.current.loadSharedAccounts()
            void handlersRef.current.refreshSharedFrontPage()
        }, anySyncing ? POLL_SYNCING_MS : POLL_MS)
        return () => window.clearInterval(id)
    }, [anySyncing])

    /** An account finishing its first sync: pull its conversations in without waiting for the next tick. */
    const backfilledKey = useMemo(() => accounts.map(a => `${a.accountId}:${a.sync?.backfilledAt ?? ''}`).join('|'), [accounts])
    const previousBackfilledKey = useRef(backfilledKey)
    useEffect(() => {
        if (previousBackfilledKey.current !== backfilledKey) {
            previousBackfilledKey.current = backfilledKey
            void handlersRef.current.refreshSharedFrontPage()
        }
    }, [backfilledKey])

    useEffect(() => {
        if (!open) return
        const onDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
        }
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
        document.addEventListener('mousedown', onDown)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', onDown)
            document.removeEventListener('keydown', onKey)
        }
    }, [open])

    useEffect(() => { if (!open) setView('accounts') }, [open])

    const choose = (accountId: string | null) => {
        setSharedAccountFilter(accountId ? [accountId] : [])
        setOpen(false)
    }

    if (loaded && !accounts.length) {
        return (
            <EmptyState>
                <EmptyTitle>No LinkedIn accounts to show</EmptyTitle>
                <EmptyText>
                    The shared inbox lists the LinkedIn accounts connected to this workspace for campaigns —
                    the ones you connected, and the ones teammates have shared.
                </EmptyText>
                <TextLink type="button" onClick={() => navigate(AppRoutes.SETTINGS_SENDING_ACCOUNTS)}>
                    Connect an account in Sending accounts
                </TextLink>
            </EmptyState>
        )
    }

    const accountMeta = (account: ISharedInboxAccount) => {
        const problem = statusLabel(account.status)
        if (problem) return <Warning><AlertTriangle size={11} /> {problem}</Warning>
        const who = account.isOwner ? 'You' : account.owner?.name || 'Workspace'
        return (
            <>
                {who}
                <Dot>·</Dot>
                <Access>{account.shared ? <Users size={11} /> : <Lock size={11} />}{account.shared ? 'Shared' : 'Private'}</Access>
            </>
        )
    }

    return (
        <Root ref={rootRef}>
            <Trigger type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-haspopup="listbox" disabled={!loaded}>
                {selected ? (
                    <AvatarWrap>
                        <ProfilePhoto capitalLetters={initials(selected.name)} allowTooltip={false} width="24px" height="24px" />
                    </AvatarWrap>
                ) : (
                    <AllIcon $size={24}><Users size={13} /></AllIcon>
                )}
                <TriggerLabel>{selected ? selected.name : 'All accounts'}</TriggerLabel>
                {triggerUnread > 0 && <Unread>{triggerUnread}</Unread>}
                <Chevron $open={open}><ChevronDown size={16} /></Chevron>
            </Trigger>

            {awaitingFirstSync && !open && (
                <SyncHint>
                    <RefreshCw size={12} className="spin" />
                    {conversations?.length ? 'Syncing LinkedIn conversations…' : 'Syncing LinkedIn conversations — they will appear here shortly.'}
                </SyncHint>
            )}

            {open && view === 'accounts' && (
                <Popover role="listbox" aria-label="LinkedIn accounts">
                    <Option role="option" aria-selected={!selected} $selected={!selected} onClick={() => choose(null)}>
                        <AllIcon $size={32}><Users size={15} /></AllIcon>
                        <OptionText>
                            <OptionTitle>All accounts</OptionTitle>
                            <OptionMeta>{accounts.length} LinkedIn account{accounts.length === 1 ? '' : 's'}</OptionMeta>
                        </OptionText>
                        {totalUnread > 0 && <Unread>{totalUnread}</Unread>}
                        <CheckSlot>{!selected && <Check size={15} />}</CheckSlot>
                    </Option>

                    <SectionLabel>Accounts</SectionLabel>

                    {accounts.map(account => {
                        const isSelected = selected?.accountId === account.accountId
                        return (
                            <Option
                                key={account.accountId}
                                role="option"
                                aria-selected={isSelected}
                                $selected={isSelected}
                                onClick={() => choose(account.accountId)}
                            >
                                <AvatarWrap>
                                    <ProfilePhoto capitalLetters={initials(account.name)} allowTooltip={false} width="32px" height="32px" />
                                    <StatusDot $ok={!statusLabel(account.status)} />
                                </AvatarWrap>
                                <OptionText>
                                    <OptionTitle>{account.name}</OptionTitle>
                                    <OptionMeta>{accountMeta(account)}</OptionMeta>
                                </OptionText>
                                {account.unreadCount > 0 && <Unread>{account.unreadCount}</Unread>}
                                <CheckSlot>{isSelected && <Check size={15} />}</CheckSlot>
                            </Option>
                        )
                    })}

                    {manageable.length > 0 && (
                        <>
                            <Divider />
                            <FooterAction type="button" onClick={() => setView('manage')}>
                                <SlidersHorizontal size={14} />
                                <span>Sharing &amp; sync</span>
                                <ChevronRight size={15} />
                            </FooterAction>
                        </>
                    )}
                </Popover>
            )}

            {open && view === 'manage' && (
                <Popover aria-label="Sharing and sync">
                    <ManageHeader>
                        <BackButton type="button" onClick={() => setView('accounts')} aria-label="Back to accounts">
                            <ChevronLeft size={16} />
                        </BackButton>
                        <ManageTitle>Sharing &amp; sync</ManageTitle>
                    </ManageHeader>
                    <ManageIntro>
                        A shared account's conversations are visible to everyone in the workspace.
                        Replies always go out from the account a conversation belongs to.
                    </ManageIntro>

                    {manageable.map(account => {
                        const problem = statusLabel(account.status)
                        const syncing = account.sync?.status === 'running'
                        return (
                            <ManageRow key={account.accountId}>
                                <AvatarWrap>
                                    <ProfilePhoto capitalLetters={initials(account.name)} allowTooltip={false} width="32px" height="32px" />
                                    <StatusDot $ok={!problem} />
                                </AvatarWrap>
                                <OptionText>
                                    <OptionTitle>{account.name}</OptionTitle>
                                    <OptionMeta>
                                        {problem
                                            ? <Warning><AlertTriangle size={11} /> {problem}</Warning>
                                            : <Access>{account.shared ? <Users size={11} /> : <Lock size={11} />}{account.shared ? 'Shared with workspace' : 'Private'}</Access>}
                                    </OptionMeta>
                                    <SyncMeta>{syncLabel(account)}</SyncMeta>
                                </OptionText>
                                <TooltipWrapper tooltipText={syncing ? 'Syncing…' : 'Sync now'} placement="top">
                                    <IconButton
                                        type="button"
                                        aria-label={`Sync ${account.name} now`}
                                        disabled={syncing || !!problem}
                                        onClick={() => { void syncSharedAccountHandler(account.accountId) }}
                                    >
                                        <RefreshCw size={14} className={syncing ? 'spin' : undefined} />
                                    </IconButton>
                                </TooltipWrapper>
                                <TooltipWrapper
                                    tooltipText={account.shared ? 'Stop sharing with the workspace' : 'Share with the workspace'}
                                    placement="top"
                                >
                                    <Toggle
                                        type="button"
                                        role="switch"
                                        aria-checked={account.shared}
                                        aria-label={`Share ${account.name}'s inbox with the workspace`}
                                        $on={account.shared}
                                        onClick={() => { void setSharedAccountSharingHandler(account.accountId, !account.shared) }}
                                    >
                                        <span />
                                    </Toggle>
                                </TooltipWrapper>
                            </ManageRow>
                        )
                    })}

                    <Divider />
                    <ManageFooter>
                        Connect or remove accounts in{' '}
                        <TextLink type="button" onClick={() => navigate(AppRoutes.SETTINGS_SENDING_ACCOUNTS)}>Sending accounts</TextLink>
                    </ManageFooter>
                </Popover>
            )}
        </Root>
    )
}

const ink = '#262b3a'
const muted = '#6a7181'
const faint = '#8c96ad'
const line = '#eceef3'
const accent = '#6467f2'

const Root = styled.div`
    position: relative;
    padding: 12px 12px 0;
    flex-shrink: 0;
    font-family: 'Nunito Sans', sans-serif;

    .spin { animation: shared-inbox-spin 1s linear infinite; }
    @keyframes shared-inbox-spin { to { transform: rotate(360deg); } }
`
const Trigger = styled.button`
    width: 100%;
    height: 40px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 8px 0 8px;
    border: 1px solid #e3e7ef;
    border-radius: 10px;
    background: #ffffff;
    color: ${ink};
    cursor: pointer;
    font-family: inherit;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover:not(:disabled) { border-color: #cfd5e3; }
    &[aria-expanded='true'] { border-color: #c9cbfb; box-shadow: 0 0 0 3px #f0efff; }
    &:disabled { cursor: default; opacity: 0.6; }
`
const TriggerLabel = styled.span`
    flex: 1;
    min-width: 0;
    text-align: left;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`
const Chevron = styled.span<{ $open: boolean }>`
    display: inline-flex;
    color: ${faint};
    transform: rotate(${({ $open }) => ($open ? '180deg' : '0')});
    transition: transform 0.15s ease;
`
const AllIcon = styled.span<{ $size: number }>`
    width: ${({ $size }) => $size}px;
    height: ${({ $size }) => $size}px;
    flex-shrink: 0;
    border-radius: 50%;
    background: #f0efff;
    color: ${accent};
    display: inline-flex;
    align-items: center;
    justify-content: center;
`
/** Soft count pill — loud red circles fight with the conversation list's own unread dots. */
const Unread = styled.span`
    min-width: 22px;
    height: 20px;
    padding: 0 7px;
    border-radius: 999px;
    background: #fff0ef;
    color: #e5484d;
    font-size: 11px;
    font-weight: 800;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`
const SyncHint = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    font-size: 12px;
    color: ${muted};
`
const Popover = styled.div`
    position: absolute;
    left: 12px;
    right: 12px;
    top: calc(100% + 6px);
    z-index: 30;
    min-width: 320px;
    max-height: min(480px, 70vh);
    overflow-y: auto;
    padding: 6px;
    background: #ffffff;
    border: 1px solid ${line};
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 16px 40px rgba(38, 43, 58, 0.14);
`
const Option = styled.div<{ $selected: boolean }>`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 10px;
    cursor: pointer;
    background: ${({ $selected }) => ($selected ? '#f3f2ff' : 'transparent')};
    transition: background 0.12s ease;

    &:hover { background: ${({ $selected }) => ($selected ? '#efeeff' : '#f6f7fb')}; }
`
const AvatarWrap = styled.span`
    position: relative;
    flex-shrink: 0;
    display: inline-flex;
`
const StatusDot = styled.span<{ $ok: boolean }>`
    position: absolute;
    right: -1px;
    bottom: -1px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 2px solid #ffffff;
    background: ${({ $ok }) => ($ok ? '#2bb673' : '#f59e0b')};
`
const OptionText = styled.div`
    flex: 1;
    min-width: 0;
    line-height: 1.3;
`
const OptionTitle = styled.div`
    font-size: 13px;
    font-weight: 700;
    color: ${ink};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`
const OptionMeta = styled.div`
    display: flex;
    align-items: center;
    min-width: 0;
    margin-top: 1px;
    font-size: 12px;
    color: ${faint};
    white-space: nowrap;
    overflow: hidden;
`
const SyncMeta = styled.div`
    margin-top: 1px;
    font-size: 11px;
    color: #a3abbd;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`
const Dot = styled.span`
    margin: 0 5px;
    color: #c4c9d6;
`
const Access = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 3px;
`
const Warning = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #c2410c;
    font-weight: 600;
`
const CheckSlot = styled.span`
    width: 16px;
    flex-shrink: 0;
    display: inline-flex;
    justify-content: center;
    color: ${accent};
`
const SectionLabel = styled.div`
    padding: 10px 10px 4px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #a3abbd;
`
const Divider = styled.div`
    height: 1px;
    background: ${line};
    margin: 6px 4px;
`
const FooterAction = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 10px;
    border: 0;
    border-radius: 10px;
    background: transparent;
    color: ${muted};
    font-family: inherit;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;

    span { flex: 1; text-align: left; }
    &:hover { background: #f6f7fb; color: ${ink}; }
`
const ManageHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 4px 0;
`
const BackButton = styled.button`
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: ${muted};
    cursor: pointer;

    &:hover { background: #f6f7fb; color: ${ink}; }
`
const ManageTitle = styled.div`
    font-size: 14px;
    font-weight: 800;
    color: ${ink};
`
const ManageIntro = styled.p`
    margin: 4px 10px 8px;
    font-size: 12px;
    line-height: 1.5;
    color: ${faint};
`
const ManageRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px;
    border-radius: 10px;

    &:hover { background: #fafbfd; }
`
const IconButton = styled.button`
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: ${faint};
    cursor: pointer;

    &:hover:not(:disabled) { background: #f0efff; color: ${accent}; }
    &:disabled { cursor: default; opacity: 0.45; }
`
/** Compact on/off switch — the app-wide Switch is sized for settings pages, not a popover row. */
const Toggle = styled.button<{ $on: boolean }>`
    position: relative;
    width: 34px;
    height: 20px;
    flex-shrink: 0;
    padding: 0;
    border: 0;
    border-radius: 10px;
    background: ${({ $on }) => ($on ? accent : '#d9dde6')};
    cursor: pointer;
    transition: background 0.15s ease;

    span {
        position: absolute;
        top: 2px;
        left: ${({ $on }) => ($on ? '16px' : '2px')};
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #ffffff;
        box-shadow: 0 1px 3px rgba(16, 24, 40, 0.25);
        transition: left 0.15s ease;
    }

    &:focus-visible { outline: 2px solid #b9bbff; outline-offset: 2px; }
`
const ManageFooter = styled.div`
    padding: 6px 10px 8px;
    font-size: 12px;
    color: ${faint};
`
const TextLink = styled.button`
    padding: 0;
    border: 0;
    background: none;
    color: ${accent};
    font-family: inherit;
    font-size: inherit;
    font-weight: 700;
    cursor: pointer;

    &:hover { text-decoration: underline; }
`
const EmptyState = styled.div`
    margin: 12px 12px 0;
    padding: 14px;
    border: 1px dashed #d6deec;
    border-radius: 12px;
    background: #fafbff;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 12px;
`
const EmptyTitle = styled.div`
    font-size: 13px;
    font-weight: 700;
    color: ${ink};
    margin-bottom: 4px;
`
const EmptyText = styled.p`
    line-height: 1.5;
    color: ${muted};
    margin: 0 0 8px;
`

export default SharedAccountsPicker
